import { Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { ShopOffersPurchase, ShopOffersResponseTypes } from '@app/shared/models/shop-offers.model';
import { environment } from '@environments/environment';
import { LoggerService } from '@app/shared/services/logger.service';
import { OneSignalService, TagName } from '@app/shared/services/one-signal.service';
import { Observable } from 'rxjs';
import { SharedModule } from '@app/shared/shared.module';
import { OffersRepository } from '@app/shared/state/offer/offer.repository';
import { AdyenService } from './adyen.service';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-adyen-modal',
  templateUrl: './adyen-modal.component.html',
  styleUrls: ['./adyen-modal.component.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class AdyenModalComponent implements OnInit {
  currentPurchase: ShopOffersPurchase;
  componentDestroy: () => Observable<unknown>;

  showBackButton = signal(true);

  public readonly dialog = inject(MatDialog);
  private readonly eventManager = inject(EventManager);
  private readonly logger = inject(LoggerService);
  private readonly oneSignalService = inject(OneSignalService);
  private readonly offersRepo = inject(OffersRepository);
  private readonly adyenService = inject(AdyenService);
  public adyenModalData = inject<Modal>(MAT_DIALOG_DATA);

  ngOnInit() {
    this.currentPurchase = this.adyenModalData.data;

    queueMicrotask(() => {
      this.handleAdyenPurchase();
    });

    this.adyenService.onPaymentCompleted = async (result, component) => {
      this.showBackButton.set(false);

      const res = await this.handleServerResponse(result, component);

      if (res.completed) {
        await this.finalizeAdyenFlow();
      }
    };

    this.adyenService.onError = (error) => {
      this.logger.error(error.message);
    };
  }

  public goBack() {
    this.eventManager.closeModal(
      new Modal(NonPersistedModalName.AdyenModalComponent, ModalType.View, this.adyenModalData, '', true)
    );
  }

  private async handleAdyenPurchase() {
    const [adyen, checkout] = await Promise.all([this.adyenService.getAdyen(), this.adyenService.waitForCheckout()]);

    const dropin = new adyen.Dropin(checkout, {
      paymentMethodComponents: [adyen.Card, adyen.PayPal],
      paymentMethodsConfiguration: {
        applepay: {
          buttonType: 'buy',
          buttonColor: 'black',
          renderApplePayCodeAs: 'window',
        },
        googlepay: {
          buttonType: 'buy',
          buttonColor: 'white',
          environment: environment.adyenConfig.googlePayEnv,
        },
      },
    });

    requestAnimationFrame(() => {
      dropin.mount('#dropin-container');
    });
  }

  private async finalizeAdyenFlow(): Promise<void> {
    await this.delay(3000);

    const dialogRef = this.dialog.getDialogById(NonPersistedModalName.AdyenModalComponent);

    if (dialogRef) {
      if (this.currentPurchase.offerId) {
        this.offersRepo.removeOffer(this.currentPurchase.offerId);
      }

      this.eventManager.closeModal(new Modal(NonPersistedModalName.AdyenModalComponent, ModalType.View, {}, '', true));
    }
  }

  // eslint-disable-next-line
  private async handleServerResponse(res: any, component: any): Promise<{ completed: boolean }> {
    if (res.action != null) {
      component.handleAction(res.action);
      return { completed: false };
    }

    switch (res.resultCode) {
      case ShopOffersResponseTypes.AUTHORISED:
        this.oneSignalService.sendTag(TagName.LastPaid, new Date()).catch((e) => this.logger.error(e));
        this.logger.debug('Purchase Succeeded!');
        break;

      case ShopOffersResponseTypes.PENDING:
        this.logger.info('Purchase Pending...');
        break;

      case ShopOffersResponseTypes.RECEIVED:
        this.logger.info('Purchase Received');
        break;

      case ShopOffersResponseTypes.REFUSED:
        this.oneSignalService
          .sendTag(TagName.PurchaseFailed, { failed: true, date: new Date() })
          .catch((e) => this.logger.error(e));
        this.logger.error('Purchase Failed');
        break;

      default:
        this.oneSignalService
          .sendTag(TagName.PurchaseDataFailed, { failed: true, date: new Date() })
          .catch((e) => this.logger.error(e));
        this.logger.error('Purchase Error');
        break;
    }

    return { completed: true };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
